import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { AdminClassNotesView, ApiService, BulkNotesProgressEvent, BulkNotesCompleteEvent } from '../../../../core/services/api/api.service';
import { SocketService } from '../../../../core/services/socket/socket.service';
import { SocketEvent } from '../../../../models/enums/socket_event.enum';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { EnfafSwitchDarkComponent } from '../../../../components/enfaf-switch-dark/enfaf-switch-dark.component';
import { QuillModule } from 'ngx-quill';
import TurndownService from 'turndown';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const POLLING_INTERVAL_MS = 3000;

const TRANSCRIPT_ERROR_LABELS: Partial<Record<string, string>> = {
  fetch_error: 'Parece que no se ha podido acceder a la transcripción del vídeo. Inténtalo de nuevo más tarde.',
  access_error: 'Parece que no se ha podido acceder a la transcripción del vídeo. Inténtalo de nuevo más tarde.',
  unknown: 'Parece que no se ha podido acceder a la transcripción del vídeo. Inténtalo de nuevo más tarde.'
};

const TRANSCRIPT_ERROR_FALLBACK = 'Parece que no se ha podido acceder a la transcripción del vídeo. Inténtalo de nuevo más tarde.';

const NOTES_ERROR_LABELS: Partial<Record<string, string>> = {
  not_found: 'Clase no encontrada',
  no_transcript: 'Sin transcripción disponible',
  empty_transcript: 'Transcripción vacía',
  ai_error: 'Error del modelo IA',
  pdf_error: 'Error generando PDF',
  s3_error: 'Error subiendo a S3',
  unknown: 'Error desconocido'
};

const NOTES_ERROR_FALLBACK = 'Parece que no se ha podido generar el resumen. Inténtalo de nuevo.';

@Component({
  selector: 'app-admin-lesson-notes',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIconModule, MatProgressSpinnerModule, QuillModule, EnfafSwitchDarkComponent],
  templateUrl: './admin-lesson-notes.component.html',
  styleUrl: './admin-lesson-notes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminLessonNotesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() courseClassId!: string;
  @Input() isBulkGenerating = false;
  @Output() notesHasChanges = new EventEmitter<boolean>();
  @Output() isAutoProcessing = new EventEmitter<boolean>();
  @Output() notesReviewStateChanged = new EventEmitter<boolean>(); // emits true when pending review

  private api = inject(ApiService);
  private alert = inject(AlertService);
  private toast = inject(ToastService);
  private chRef = inject(ChangeDetectorRef);
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private socketService = inject(SocketService);
  private ngZone = inject(NgZone);

  constructor() {
    this.iconRegistry.addSvgIcon('other_icons', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/other_icons.svg'));
    this.iconRegistry.addSvgIcon('other_icons_warning', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/other_icons_warning.svg'));
  }

  notesView: AdminClassNotesView | null = null;
  publishedControl = new FormControl<boolean>(true, { nonNullable: true });
  isLoading = false;
  isSaving = false;
  isDeleting = false;
  isGeneratingNotes = false;
  isGeneratingTranscript = false;
  isMarkingReviewed = false;
  isCardExpanded = true;
  isBulkBlocked = false;
  editorContent = '';
  savedEditorContent = '';
  savedPublished = false;
  isAutoFlow = false;
  private isFirstGeneration = false;

  private pollingInterval: ReturnType<typeof setInterval> | null = null;

  readonly quillModules = {
    toolbar: [['bold', 'italic', 'underline']]
  };

  private readonly turndown = new TurndownService({ headingStyle: 'atx', bulletListMarker: '-' }).addRule('underline', {
    filter: ['u'],
    replacement: (content: string) => `<u>${content}</u>`
  });

  get isDirty(): boolean {
    return this.editorContent !== this.savedEditorContent || (this.notesView?.notesPublished ?? false) !== this.savedPublished;
  }

  onEditorContentChange(event: { source: string }): void {
    if (event?.source === 'api') return;
    this.notesHasChanges.emit(this.isDirty);
    this.chRef.markForCheck();
  }

  onEditorCreated(quill: any): void {
    const toolbar = quill.getModule('toolbar');
    toolbar.addHandler('italic', (value: boolean) => {
      if (value) quill.format('underline', false);
      quill.format('italic', value);
    });
    toolbar.addHandler('underline', (value: boolean) => {
      if (value) quill.format('italic', false);
      quill.format('underline', value);
    });
  }

  private markdownToHtml(markdown: string): string {
    // Run all content through marked — it passes HTML tags through as-is,
    // so both legacy HTML content and markdown-with-inline-HTML render correctly.
    const raw = marked.parse(markdown, { async: false }) as string;
    return DOMPurify.sanitize(raw);
  }

  readonly notesErrorLabels = NOTES_ERROR_LABELS;
  readonly notesErrorFallback = NOTES_ERROR_FALLBACK;
  readonly transcriptErrorFallback = TRANSCRIPT_ERROR_FALLBACK;
  readonly transcriptErrorLabels = TRANSCRIPT_ERROR_LABELS;

  private bulkProgressHandler = (data: BulkNotesProgressEvent): void => {
    if (data.courseClassId === this.courseClassId && data.status === 'completed') {
      this.ngZone.run(() => {
        this.isBulkBlocked = false;
        this.loadNotesView();
      });
    }
  };

  private bulkCompleteHandler = (data: BulkNotesCompleteEvent): void => {
    // Reload if this class was part of the completed bulk run
    const wasProcessed = data.results?.some((r) => r.courseClassId === this.courseClassId);
    if (wasProcessed) {
      this.ngZone.run(() => {
        this.isBulkBlocked = false;
        this.loadNotesView();
      });
    }
  };

  ngOnInit(): void {
    this.loadNotesView();
    this.socketService.socket.on(SocketEvent.BulkNotesGenerationProgress, this.bulkProgressHandler);
    this.socketService.socket.on(SocketEvent.BulkNotesGenerationComplete, this.bulkCompleteHandler);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isBulkGenerating']) {
      this.isBulkBlocked = this.isBulkGenerating;
      this.chRef.markForCheck();
    }
    if (changes['courseClassId'] && !changes['courseClassId'].firstChange) {
      this.stopPolling();
      this.notesView = null;
      this.isCardExpanded = true;
      this.editorContent = '';
      this.savedEditorContent = '';
      this.savedPublished = false;
      if (this.isAutoFlow) {
        this.isAutoFlow = false;
        this.isAutoProcessing.emit(false);
      }
      this.loadNotesView();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    if (this.isAutoFlow) {
      this.isAutoProcessing.emit(false);
    }
    this.socketService.socket.off(SocketEvent.BulkNotesGenerationProgress, this.bulkProgressHandler);
    this.socketService.socket.off(SocketEvent.BulkNotesGenerationComplete, this.bulkCompleteHandler);
  }

  async loadNotesView(): Promise<void> {
    if (!this.courseClassId) return;
    this.isLoading = true;
    this.chRef.markForCheck();
    try {
      const view = await firstValueFrom(this.api.getAdminClassNotesView(this.courseClassId));
      this.notesView = view;
      this.publishedControl.setValue(view.notesPublished ?? true);
      this.savedPublished = view.notesPublished ?? true;
      if (view.notesText) {
        this.editorContent = this.markdownToHtml(view.notesText);
        this.savedEditorContent = this.editorContent;
      }

      // Start auto-flow only when actively processing or when the class is brand-new
      // (both transcript and notes still pending, meaning Vimeo ID was just set).
      // Do NOT auto-flow when transcript is ready but notes are pending: that state
      // also occurs after a manual deletion, which would undesirably re-generate on reload.
      const shouldAutoFlow =
        (view.transcriptStatus === 'pending' && view.notesStatus === 'pending') ||
        view.transcriptStatus === 'processing' ||
        view.notesStatus === 'processing';

      if (shouldAutoFlow) {
        this.isAutoFlow = true;
        this.isAutoProcessing.emit(true);
        // If notes are already being generated on load (no prior text), treat it as first creation
        if (view.notesStatus === 'processing' && !view.notesText) {
          this.isFirstGeneration = true;
        }
      }

      this.startPollingIfNeeded();

      // Notify parent of current review state so the accordion dot stays in sync
      this.notesReviewStateChanged.emit(!view.notesReviewedAt && view.notesStatus === 'ready');

      // Fire-and-forget trigger; each method has its own error handling
      if (this.isAutoFlow) {
        if (view.transcriptStatus === 'pending') {
          this.generateTranscript();
        }
        // Note: transcriptStatus === 'ready' && notesStatus === 'pending' is NOT triggered here.
        // If the pipeline was mid-flight when the page loaded, the polling will pick it up once
        // the transcript transitions from 'processing' to 'ready'.
      }

      // Safety net: if notes are already ready but somehow not published (e.g. the component
      // loaded after the generation finished and missed the polling transition), auto-publish now.
      // The backend now sets notesPublished = true directly, so this should rarely be needed.
      if (view.notesStatus === 'ready' && view.notesText && !view.notesPublished) {
        firstValueFrom(this.api.toggleClassNotesPublished({ courseClassId: this.courseClassId, notesPublished: true }))
          .then(() => {
            this.notesView = this.notesView ? { ...this.notesView, notesPublished: true } : null;
            this.publishedControl.setValue(true);
            this.savedPublished = true;
            this.chRef.markForCheck();
          })
          .catch(() => {
            /* silent – admin can toggle manually */
          });
      }
    } catch {
      this.toast.presentErrorToast('Error cargando los apuntes');
      if (this.isAutoFlow) {
        this.isAutoFlow = false;
        this.isAutoProcessing.emit(false);
      }
    } finally {
      this.isLoading = false;
      this.chRef.markForCheck();
    }
  }

  get isPendingReady(): boolean {
    return !this.isAutoFlow && !!this.notesView && this.notesView.notesStatus === 'pending' && this.notesView.transcriptStatus === 'ready';
  }

  /** True when notes are ready and have NOT been reviewed yet (no reviewedAt date). */
  get isPendingReview(): boolean {
    return !!this.notesView && this.notesView.notesStatus === 'ready' && !this.notesView.notesReviewedAt;
  }

  get pendingReviewSubtitle(): string {
    return this.notesView?.notesProvider === 'admin' ? 'Pendiente de revisión' : 'Generado automáticamente - Pendiente de revisión';
  }

  toggleCard(): void {
    if (this.isPendingReady) return;
    this.isCardExpanded = !this.isCardExpanded;
    this.chRef.markForCheck();
  }

  async confirmDeleteNotes(): Promise<void> {
    const confirmed = await this.alert.presentConfirmRemoveActionAlert({
      title: 'Eliminar resumen',
      message: '¿Eliminar el resumen generado por IA?',
      message2: 'Esta acción no se puede deshacer.',
      cancelText: 'Cancelar',
      confirmText: 'Eliminar'
    });
    if (confirmed) await this.deleteNotes();
  }

  async deleteNotes(): Promise<void> {
    if (this.isDeleting || !this.courseClassId) return;
    this.isDeleting = true;
    this.chRef.markForCheck();
    try {
      await firstValueFrom(this.api.deleteAdminClassNotes(this.courseClassId));
      this.notesView = this.notesView ? { ...this.notesView, notesStatus: 'pending', notesText: null, notesPublished: false } : null;
      this.publishedControl.setValue(false);
      this.editorContent = '';
      this.savedEditorContent = '';
      this.savedPublished = false;
      this.notesHasChanges.emit(false);
    } catch {
      this.toast.presentErrorToast('Error eliminando el resumen');
    } finally {
      this.isDeleting = false;
      this.chRef.markForCheck();
    }
  }

  async markAsReviewed(): Promise<void> {
    if (this.isMarkingReviewed || !this.courseClassId) return;
    this.isMarkingReviewed = true;
    this.chRef.markForCheck();
    try {
      const result = await firstValueFrom(this.api.markClassNotesAsReviewed(this.courseClassId));
      if (this.notesView) {
        this.notesView = { ...this.notesView, notesReviewedAt: result.notesReviewedAt, notesReviewedBy: result.notesReviewedBy };
      }
      this.notesReviewStateChanged.emit(false);
      this.toast.presentSuccessToast('Resumen marcado como revisado');
    } catch {
      this.toast.presentErrorToast('Error al marcar el resumen como revisado');
    } finally {
      this.isMarkingReviewed = false;
      this.chRef.markForCheck();
    }
  }

  async saveChanges(): Promise<void> {
    if (this.isSaving || !this.courseClassId || !this.notesView) return;
    this.isSaving = true;
    this.chRef.markForCheck();
    try {
      const textChanged = this.editorContent !== this.savedEditorContent;
      const publishedChanged = this.notesView.notesPublished !== this.savedPublished;

      if (textChanged) {
        const markdownContent = this.turndown.turndown(this.editorContent);
        const result = await firstValueFrom(this.api.updateAdminClassNotesContent(this.courseClassId, markdownContent));
        const newHtml = this.markdownToHtml(result.notesText);
        this.notesView = {
          ...this.notesView,
          notesStatus: 'ready',
          notesText: result.notesText,
          notesUpdatedAt: result.notesUpdatedAt,
          notesProvider: result.notesProvider
        };
        this.editorContent = newHtml;
        this.savedEditorContent = newHtml;
      }

      if (publishedChanged) {
        await firstValueFrom(
          this.api.toggleClassNotesPublished({
            courseClassId: this.courseClassId,
            notesPublished: this.notesView.notesPublished
          })
        );
        this.savedPublished = this.notesView.notesPublished;
      }

      this.notesHasChanges.emit(false);
    } catch {
      this.toast.presentErrorToast('Error guardando los apuntes');
    } finally {
      this.isSaving = false;
      this.chRef.markForCheck();
    }
  }

  async generateNotes(): Promise<void> {
    if (this.isGeneratingNotes || !this.courseClassId) return;
    this.isGeneratingNotes = true;
    this.chRef.markForCheck();
    try {
      this.isFirstGeneration = !this.notesView?.notesText;
      await firstValueFrom(this.api.generateClassNotesAdmin(this.courseClassId));
      if (this.notesView) {
        this.notesView = { ...this.notesView, notesStatus: 'processing' };
      }
      this.startPolling();
    } catch {
      this.toast.presentErrorToast('Error iniciando la generación del resumen');
      if (this.isAutoFlow) {
        this.isAutoFlow = false;
        this.isAutoProcessing.emit(false);
      }
    } finally {
      this.isGeneratingNotes = false;
      this.chRef.markForCheck();
    }
  }

  async generateTranscript(): Promise<void> {
    if (this.isGeneratingTranscript || !this.courseClassId) return;
    this.isGeneratingTranscript = true;
    this.chRef.markForCheck();
    try {
      await firstValueFrom(this.api.generateClassTranscriptAdmin(this.courseClassId));
      if (this.notesView) {
        this.notesView = { ...this.notesView, transcriptStatus: 'processing' };
      }
      this.startPolling();
    } catch {
      this.toast.presentErrorToast('Error iniciando la generación de la transcripción');
      if (this.isAutoFlow) {
        this.isAutoFlow = false;
        this.isAutoProcessing.emit(false);
      }
    } finally {
      this.isGeneratingTranscript = false;
      this.chRef.markForCheck();
    }
  }

  togglePublished(): void {
    if (!this.notesView) return;
    // publishedControl is already updated internally by EnfafSwitchDarkComponent before (onChange) fires
    this.notesView = { ...this.notesView, notesPublished: this.publishedControl.value };
    this.notesHasChanges.emit(this.isDirty);
    this.chRef.markForCheck();
  }

  private startPollingIfNeeded(): void {
    if (this.notesView?.notesStatus === 'processing' || this.notesView?.transcriptStatus === 'processing') {
      this.startPolling();
    }
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollingInterval = setInterval(async () => {
      try {
        const view = await firstValueFrom(this.api.getAdminClassNotesView(this.courseClassId));
        const wasTranscriptProcessing = this.notesView?.transcriptStatus === 'processing';
        const wasNotesProcessing = this.notesView?.notesStatus === 'processing';
        // Preserve local unpersisted toggle change before overwriting with backend state
        const hadLocalPublishedChange = (this.notesView?.notesPublished ?? false) !== this.savedPublished;
        const localPublished = this.publishedControl.value;
        this.notesView = view;
        if (hadLocalPublishedChange) {
          // Restore local intent so the user's pending toggle is not lost
          this.notesView = { ...this.notesView, notesPublished: localPublished };
        } else {
          this.publishedControl.setValue(view.notesPublished ?? true);
          this.savedPublished = view.notesPublished ?? true;
        }
        const isStillProcessing = view.notesStatus === 'processing' || view.transcriptStatus === 'processing';

        if (wasNotesProcessing && !isStillProcessing && view.notesText) {
          // New AI-generated content arrived
          if (this.isFirstGeneration) {
            // Enable publish by default only on first creation and persist to DB
            this.notesView = { ...this.notesView, notesPublished: true };
            this.publishedControl.setValue(true);
            this.isFirstGeneration = false;
            // savedPublished is intentionally not set here: it is set only after the API confirms.
            // This way, if the call fails, isDirty remains true and saveChanges() will retry the toggle.
            firstValueFrom(this.api.toggleClassNotesPublished({ courseClassId: this.courseClassId, notesPublished: true }))
              .then(() => {
                this.savedPublished = true;
              })
              .catch(() => this.toast.presentErrorToast('Error al publicar el resumen automáticamente'));
          }
          this.editorContent = this.markdownToHtml(view.notesText);
          this.notesHasChanges.emit(true);
        }

        // Auto-start notes generation when transcript completes during auto-flow
        if (this.isAutoFlow && wasTranscriptProcessing && view.transcriptStatus === 'ready' && view.notesStatus === 'pending') {
          this.generateNotes(); // starts a new polling interval internally
          this.chRef.markForCheck();
          return; // exit before stopPolling() to preserve the new interval
        }

        if (!isStillProcessing) {
          this.stopPolling();
          // Notify parent of review state so the accordion dot reflects the post-regeneration state
          this.notesReviewStateChanged.emit(!view.notesReviewedAt && view.notesStatus === 'ready');
          if (this.isAutoFlow) {
            this.isAutoFlow = false;
            this.isAutoProcessing.emit(false);
          }
        }
        this.chRef.markForCheck();
      } catch {
        // silent fail during polling
      }
    }, POLLING_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollingInterval !== null) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  isProcessing(): boolean {
    return this.notesView?.notesStatus === 'processing' || this.notesView?.transcriptStatus === 'processing';
  }

  getNotesErrorLabel(code: string | null): string {
    if (!code) return 'Error';
    return this.notesErrorLabels[code] || code;
  }
}
